import { useRef, useState, useEffect } from "react";
import { Play, Pause, Languages, Loader2, Volume2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { formatDuration } from "@/lib/format";

// Deterministic pseudo-waveform (stable per clip) so every note looks distinct
// without decoding the audio.
function barsFor(src, n = 34) {
  let h = 2166136261;
  const s = src || "x";
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  const out = [];
  for (let i = 0; i < n; i++) {
    h = (Math.imul(h, 1103515245) + 12345) & 0x7fffffff;
    out.push(0.28 + ((h % 1000) / 1000) * 0.72);
  }
  return out;
}

// WhatsApp-style voice-note player. On the customer side (hideOriginal), a
// received voice note is played ONLY in the customer's language — the agent's
// original audio is never exposed; while the translation is being prepared a
// short placeholder shows instead.
export function VoiceNote({ src, duration, mine, dark, onTranslate, cached, hideOriginal = false }) {
  const audioRef = useRef(null);
  const ttsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(duration || 0);
  const [rate, setRate] = useState(1);
  const [result, setResult] = useState(cached || null);
  const [translating, setTranslating] = useState(false);
  const [fallback, setFallback] = useState(false);

  useEffect(() => { if (cached) setResult(cached); }, [cached]);

  const translatedAudio = result && result.tts_url ? mediaUrl(result.tts_url) : null;
  const useTranslated = hideOriginal && translatedAudio;               // play the translated audio
  const showTranslating = hideOriginal && !translatedAudio && !fallback; // waiting for it
  const playSrc = useTranslated ? translatedAudio : src;
  const bars = barsFor(playSrc);

  // If we hide the original but the translation never arrives, fall back to it.
  useEffect(() => {
    if (!showTranslating) return;
    const t = setTimeout(() => setFallback(true), 15000);
    return () => clearTimeout(t);
  }, [showTranslating]);

  const doTranslate = async () => {
    if (!onTranslate || translating) return;
    setTranslating(true);
    try {
      const r = await onTranslate();
      if (r) setResult(r);
    } finally {
      setTranslating(false);
    }
  };
  const playTts = () => {
    if (ttsRef.current) { ttsRef.current.currentTime = 0; ttsRef.current.play().catch(() => {}); }
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime || 0);
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onMeta = () => { if (isFinite(a.duration) && a.duration > 0) setTotal(a.duration); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", () => setPlaying(false));
    a.addEventListener("play", () => setPlaying(true));
    a.addEventListener("loadedmetadata", onMeta);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); }
    else { a.playbackRate = rate; a.play().catch(() => {}); }
  };
  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !total) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * total;
    setCur(a.currentTime);
  };

  const fg = mine ? (dark ? "#4ade80" : "#0e8f7e") : (dark ? "#cbd5cf" : "#667781");
  const dim = mine ? (dark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.22)") : (dark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.2)");

  // Customer is waiting for the translated voice note — never show the original.
  if (showTranslating) {
    return (
      <div className="flex min-w-[200px] items-center gap-2 py-1.5 text-[12px]" style={{ color: fg }} data-testid="voice-translating">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>Preparing voice note…</span>
      </div>
    );
  }

  const progress = total ? Math.min(1, cur / total) : 0;
  const played = Math.round(progress * bars.length);
  const showElapsed = playing || cur > 0.2;
  const timeLabel = formatDuration(Math.round(showElapsed ? cur : total || duration || 0));

  return (
    <div data-testid="message-voice">
      <div className="flex min-w-[210px] max-w-[270px] items-center gap-2.5 py-0.5">
        <audio ref={audioRef} src={playSrc} preload="metadata" />
        <button
          onClick={toggle}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform active:scale-90"
          style={{ background: fg }}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="voice-play-button"
        >
          {playing ? <Pause className="h-4 w-4 fill-current" aria-hidden="true" /> : <Play className="h-4 w-4 translate-x-[1px] fill-current" aria-hidden="true" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex h-8 cursor-pointer items-center gap-[2px]" onClick={seek} data-testid="voice-waveform">
            {bars.map((b, i) => (
              <span
                key={i}
                className="flex-1 rounded-full transition-colors"
                style={{ height: `${Math.round(b * 100)}%`, minHeight: 3, background: i < played ? fg : dim }}
              />
            ))}
          </div>
          <div className="mt-0.5 flex items-center justify-between text-[10.5px] font-medium" style={{ color: dim }}>
            <span>{timeLabel}</span>
            <button onClick={cycleRate} className="rounded px-1 font-semibold" style={{ color: fg }} aria-label="Playback speed">{rate}×</button>
          </div>
        </div>
      </div>

      {/* Customer only HEARS the translated voice note — no transcript/caption
          text is shown to them. */}

      {/* Agent: manual translate button when nothing is cached yet. */}
      {!hideOriginal && onTranslate && !result && (
        <button
          onClick={doTranslate}
          disabled={translating}
          className={`mt-1 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${dark ? "bg-white/10 text-white/80 hover:bg-white/15" : "bg-black/5 text-black/70 hover:bg-black/10"}`}
          data-testid="voice-translate-button"
        >
          {translating ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : <Languages className="h-3 w-3" aria-hidden="true" />}
          {translating ? "Translating…" : "Translate"}
        </button>
      )}

      {/* Agent: translation panel (text + play the translated speech). */}
      {!hideOriginal && result && (
        <div className={`mt-1.5 rounded-lg border p-2 ${dark ? "border-white/10 bg-white/[0.04]" : "border-black/5 bg-black/[0.03]"}`} data-testid="voice-translation">
          <p className={`text-[13px] leading-snug ${dark ? "text-gray-100" : "text-[#111b18]"}`}>{result.translation}</p>
          {result.transcript && result.transcript !== result.translation && (
            <p className={`mt-1 text-[11px] italic leading-snug ${dark ? "text-white/40" : "text-black/40"}`}>{result.transcript}</p>
          )}
          {result.tts_url && (
            <>
              <audio ref={ttsRef} src={mediaUrl(result.tts_url)} preload="none" />
              <button
                onClick={playTts}
                className="mt-1.5 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white"
                style={{ background: fg }}
                data-testid="voice-tts-play"
              >
                <Volume2 className="h-3 w-3" aria-hidden="true" /> Play translation
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
